import { BaseApiService, API_CONFIG } from './base.service';
import {
  Invoice,
  InvoiceGenerationRequest,
  InvoiceGenerationResponse,
  PaginatedResponse,
  InvoiceWalletMovementsResponse,
  AlignBankTransferResponse,
  AlignBankTransferConflictResponse
} from '../../types';

// Pagination safeguards for invoices endpoint
const INVOICES_MAX_PAGE_SIZE = 1000;

export interface InvoiceListOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  customerId?: number;
  contractId?: string;
  paymentStatus?: string;
  show_paid?: boolean;
  show_unpaid?: boolean;
  show_overdue?: boolean;
  show_draft?: boolean;
  show_finalized?: boolean;
  show_cancelled?: boolean;
  show_voided?: boolean;
  from_date?: string;
  to_date?: string;
  currencies?: string[];
}

export interface InvoiceDateRangeExportStartResponse {
  task_id: string;
  status: string;
  message: string;
  status_url: string;
  download_url: string;
}

export interface InvoiceDateRangeExportStatusResponse {
  task_id: string;
  status: string;
  message?: string;
  error?: string | null;
  ready: boolean;
  download_url?: string | null;
  result?: {
    total_invoices?: number;
    exported_count?: number;
    failed_count?: number;
    currencies?: Record<string, number>;
    zip_filename?: string;
    zip_size_bytes?: number;
  } | null;
}

/**
 * Service for managing invoices
 */
export class InvoiceService extends BaseApiService {
  async createInvoice(request: InvoiceGenerationRequest): Promise<InvoiceGenerationResponse> {
    return this.post(`${API_CONFIG.ENDPOINTS.INVOICES}/`, request);
  }

  async getInvoice(id: string): Promise<Invoice> {
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/${id}`);
  }

  async updateInvoice(id: string, invoice: Partial<Invoice>): Promise<Invoice> {
    return this.put(`${API_CONFIG.ENDPOINTS.INVOICES}/${id}`, invoice);
  }

  async updateInvoiceStatus(id: string, data: { invoice_status: string; invoice_updatedby: string }): Promise<Invoice> {
    // Ensure the invoice_status is sent in lowercase to match backend expectations
    const payload = {
      ...data,
      invoice_status: data.invoice_status?.toLowerCase() || 'draft'
    };
    return this.put(`${API_CONFIG.ENDPOINTS.INVOICES}/${id}/status`, payload);
  }

  async getInvoiceByName(name: string): Promise<Invoice> {
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/name/${name}`);
  }

  async sendTestEmail(
    invoiceId: string,
    recipientEmail: string,
    language: string,
    emailType: string = 'invoice'
  ): Promise<{
    success: boolean;
    message: string;
    email_id?: string;
  }> {
    const payload = {
      recipient_email: recipientEmail,
      language: language,
      send_test: true
    };
    return this.post(`${API_CONFIG.ENDPOINTS.INVOICES}/send-invoice-email-alt/${invoiceId}`, payload);
  }

  async getInvoices(
    page: number = 1, 
    pageSize: number = 100,
    search?: string,
    status?: string,
    customerId?: number,
    contractId?: string,
    paymentStatus?: string
  ): Promise<PaginatedResponse<Invoice>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    if (search) {
      params.append('search', search);
    }
    
    if (status) {
      params.append('status', status);
    }
    
    if (customerId) {
      params.append('customer_id', customerId.toString());
    }
    
    if (contractId) {
      params.append('contract_id', contractId);
    }
    
    if (paymentStatus) {
      params.append('payment_status', paymentStatus);
    }
    
    // The backend returns an array directly, so we need to wrap it in a paginated response
    const response = await this.get<Invoice[] | PaginatedResponse<Invoice>>(`${API_CONFIG.ENDPOINTS.INVOICES}?${params.toString()}`);
    
    // Debug log to understand the response structure
    console.log('Invoice service - raw response:', response);
    
    // Check if response is already paginated or just an array
    if (Array.isArray(response)) {
      // Response is an array, wrap it in paginated response
      const items = response as Invoice[];
      const hasMore = items.length === pageSize;
      const estimatedTotal = hasMore ? (page * pageSize) + 1 : ((page - 1) * pageSize) + items.length;
      
      return {
        items: items,
        total: estimatedTotal,
        page: page,
        pageSize: pageSize,
        totalPages: Math.ceil(estimatedTotal / pageSize)
      };
    } else if (response && typeof response === 'object' && 'items' in response) {
      // Response is already paginated
      return response as PaginatedResponse<Invoice>;
    } else {
      // Unexpected format, return empty paginated response
      console.warn('Unexpected invoice response format:', response);
      return {
        items: [],
        total: 0,
        page: page,
        pageSize: pageSize,
        totalPages: 0
      };
    }
  }

  // New helper: list invoices with extended filter options mapped to backend expectations
  async listInvoices(options: InvoiceListOptions = {}): Promise<Invoice[]> {
    const {
      page = 1,
      pageSize = 100,
      search,
      status,
      customerId,
      contractId,
      paymentStatus,
      show_paid,
      show_unpaid,
      show_overdue,
      show_draft,
      show_finalized,
      show_cancelled,
      show_voided,
      from_date,
      to_date,
      currencies,
    } = options;

    // Enforce backend maximum page size
    const effectivePageSize = Math.min(pageSize ?? 100, INVOICES_MAX_PAGE_SIZE);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(effectivePageSize));
    if (search && search.trim()) params.append('search', search.trim());
    if (status) params.append('status', status);
    if (typeof customerId === 'number') params.append('customer_id', String(customerId));
    if (contractId) params.append('contract_id', contractId);
    if (paymentStatus) params.append('payment_status', paymentStatus);
    // Only append show_* flags when true (mirrors previous behavior)
    if (show_paid === true) params.append('show_paid', 'true');
    if (show_unpaid === true) params.append('show_unpaid', 'true');
    if (show_overdue === true) params.append('show_overdue', 'true');
    if (show_draft === true) params.append('show_draft', 'true');
    if (show_finalized === true) params.append('show_finalized', 'true');
    if (show_cancelled === true) params.append('show_cancelled', 'true');
    if (show_voided === true) params.append('show_voided', 'true');
    if (from_date) params.append('from_date', from_date);
    if (to_date) params.append('to_date', to_date);
    if (currencies && currencies.length > 0) {
      params.append('currencies', currencies.join(','));
    }
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/?${params.toString()}`);
  }

  // Auto-pagination helper to fetch all invoices across pages respecting backend limits
  async listInvoicesAll(options: InvoiceListOptions & { maxPages?: number } = {}): Promise<Invoice[]> {
    const batchSize = Math.min(options.pageSize ?? INVOICES_MAX_PAGE_SIZE, INVOICES_MAX_PAGE_SIZE);
    let page = options.page ?? 1;
    const maxPages = options.maxPages ?? 1000; // safety bound
    const all: Invoice[] = [];

    for (let i = 0; i < maxPages; i++) {
      const items = await this.listInvoices({ ...options, page, pageSize: batchSize });
      if (!items || items.length === 0) break;
      all.push(...items);
      if (items.length < batchSize) break;
      page += 1;
    }

    return all;
  }

  async getInvoicesByContract(contractId: string): Promise<Invoice[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/contract/${contractId}`);
  }

  async getInvoicesByCustomer(customerId: number): Promise<Invoice[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/customer/${customerId}`);
  }

  async getInvoicesByContractsBatch(contractIds: string[]): Promise<Record<string, Invoice[]>> {
    return this.post(`${API_CONFIG.ENDPOINTS.INVOICES}/contracts/batch`, contractIds);
  }

  async downloadContractInvoiceReport(
    contractId: string,
    language: string = 'en',
    finalizedOnly: boolean = true
  ): Promise<Blob> {
    // Build URL with query parameters
    const params = new URLSearchParams({
      language,
      finalized_only: finalizedOnly.toString()
    });
    
    // Use the base service's axios instance for proper auth handling
    const url = `${API_CONFIG.ENDPOINTS.CONTRACTS}/${contractId}/report?${params.toString()}`;
    
    const response = await this.api.get(url, {
      responseType: 'blob'
    });
    
    return response.data;
  }

  async deleteInvoice(id: string): Promise<{ message: string }> {
    return this.delete(`${API_CONFIG.ENDPOINTS.INVOICES}/${id}`);
  }

  // Payment status management
  async updateAllPaymentStatuses(): Promise<{
    success: boolean;
    message: string;
    updates: {
      overdue: number;
      pending: number;
      errors: string[];
    };
  }> {
    return this.post(`${API_CONFIG.ENDPOINTS.INVOICES}/payment-status/update-all`, {});
  }

  // New: Check payments (bulk) - optionally pass a list of invoice_ids to scope the check
  async checkPayments(invoiceIds?: string[]): Promise<{
    success: boolean;
    checked: number;
    updated: number;
    errors?: string[];
    details?: any;
  }> {
    const payload = invoiceIds && invoiceIds.length > 0 ? { invoice_ids: invoiceIds } : {};
    return this.post(`${API_CONFIG.ENDPOINTS.BULK_INVOICES}/check-payments`, payload);
  }

  async getOverdueInvoices(daysOverdue?: number): Promise<Invoice[]> {
    const params = daysOverdue ? `?days_overdue=${daysOverdue}` : '';
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/payment-status/overdue${params}`);
  }

  async getUpcomingDueInvoices(daysAhead: number = 7): Promise<Invoice[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/payment-status/upcoming-due?days_ahead=${daysAhead}`);
  }

  async getPaymentStatusSummary(): Promise<Record<string, number>> {
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/payment-status/summary`);
  }

  async getInvoiceBoletos(invoiceId: string): Promise<any[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.INVOICES}/${invoiceId}/boletos`);
  }

  // Wallet movements around invoice due date window (positive only)
  async getInvoiceWalletMovements(
    invoiceId: string,
    opts: { walletId?: string; daysBefore?: number; daysAfter?: number } = {}
  ): Promise<InvoiceWalletMovementsResponse> {
    const { walletId, daysBefore = 30, daysAfter = 30 } = opts;
    const params = new URLSearchParams();
    if (typeof daysBefore === 'number') params.append('days_before', String(daysBefore));
    if (typeof daysAfter === 'number') params.append('days_after', String(daysAfter));
    if (walletId) params.append('wallet_id', walletId);
    return this.get(`/api/v1/bulk-invoices/invoices/${invoiceId}/wallet-movements?${params.toString()}`);
  }

  // Wallet movements within explicit date range (week-by-week loading support)
  async getInvoiceWalletMovementsRange(
    invoiceId: string,
    opts: { walletId?: string; fromDate: string; toDate: string; sync?: boolean }
  ): Promise<InvoiceWalletMovementsResponse> {
    const { walletId, fromDate, toDate, sync = true } = opts;
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (walletId) params.append('wallet_id', walletId);
    if (sync) params.append('sync', 'true');
    return this.get(`/api/v1/bulk-invoices/invoices/${invoiceId}/wallet-movements?${params.toString()}`);
  }

  // Align a specific movement to an invoice; when backend returns 409, we surface requires_confirmation
  async alignBankTransfer(
    invoiceId: string,
    movementId: string,
    force?: boolean
  ): Promise<AlignBankTransferResponse | AlignBankTransferConflictResponse> {
    try {
      return await this.post(`/api/v1/bulk-invoices/invoices/${invoiceId}/align-bank-transfer`, {
        movement_id: movementId,
        force: !!force,
      });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        return err.response.data as AlignBankTransferConflictResponse;
      }
      throw err;
    }
  }

  // Disalign a movement: unmatch and set invoice back to unpaid
  async disalignBankTransfer(
    invoiceId: string,
    movementId: string,
  ): Promise<{ success: boolean; updated: boolean; invoice_id: string; movement_id: string } | any> {
    return this.post(`/api/v1/bulk-invoices/invoices/${invoiceId}/disalign-bank-transfer`, {
      movement_id: movementId,
    });
  }

  async startInvoiceDateRangeExport(
    fromDate: string,
    toDate: string,
    currencies?: string[]
  ): Promise<InvoiceDateRangeExportStartResponse> {
    return this.post(`${API_CONFIG.ENDPOINTS.BULK_INVOICES}/export-by-invoice-date`, {
      from_date: fromDate,
      to_date: toDate,
      currencies: currencies && currencies.length > 0 ? currencies : undefined,
    });
  }

  async getInvoiceDateRangeExportStatus(taskId: string): Promise<InvoiceDateRangeExportStatusResponse> {
    return this.get(`${API_CONFIG.ENDPOINTS.BULK_INVOICES}/export-by-invoice-date/${taskId}`);
  }

  async downloadInvoiceDateRangeExport(taskId: string, filename?: string): Promise<void> {
    const response = await this.axiosInstance.get(
      `${API_CONFIG.ENDPOINTS.BULK_INVOICES}/export-by-invoice-date/${taskId}/download`,
      {
        responseType: 'blob',
        timeout: 30 * 60 * 1000, // allow large exports to complete
      }
    );

    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
    const quoted = contentDisposition?.match(/filename="([^"]+)"/i)?.[1];
    const plain = contentDisposition?.match(/filename=([^;]+)/i)?.[1]?.trim();
    const resolvedFilename = filename || quoted || plain || `invoices-export-${taskId}.zip`;

    link.download = resolvedFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const invoiceService = new InvoiceService();
